"use client";

import { useState, useEffect, useCallback } from "react";

interface MenuCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  color: string | null;
  _count: { items: number };
}

interface MenuItem {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price: number;
  cost: number | null;
  image: string | null;
  itemType: string;
  isActive: boolean;
  isPopular: boolean;
  sortOrder: number;
  station: string | null;
  prepTime: number | null;
  sku: string | null;
  barcode: string | null;
  trackInventory: boolean;
  categoryId: string;
  category: { id: string; name: string };
  modifierGroups?: ModifierGroup[];
}

interface ModifierGroup {
  id: string;
  name: string;
  type: string;
  minSelect: number;
  maxSelect: number;
  isRequired: boolean;
  modifiers: Modifier[];
}

interface Modifier {
  id: string;
  name: string;
  price: number;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

interface UseMenuOptions {
  categoryId?: string | null;
  search?: string;
  includeInactive?: boolean;
}

export function useCategories(includeInactive = false) {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (includeInactive) params.set("includeInactive", "true");
      const res = await fetch(`/api/menu/categories?${params}`);
      if (!res.ok) throw new Error("Failed to fetch categories");
      const data = await res.json();
      setCategories(data.categories || data.data || data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const createCategory = async (data: Record<string, unknown>) => {
    const res = await fetch("/api/menu/categories", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to create category");
    }
    await fetchCategories();
    return res.json();
  };

  const updateCategory = async (id: string, data: Record<string, unknown>) => {
    const res = await fetch(`/api/menu/categories/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to update category");
    }
    await fetchCategories();
    return res.json();
  };

  const deleteCategory = async (id: string) => {
    const res = await fetch(`/api/menu/categories/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to delete category");
    }
    await fetchCategories();
  };

  return { categories, loading, error, fetchCategories, createCategory, updateCategory, deleteCategory };
}

export function useMenuItems(options: UseMenuOptions = {}) {
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (options.categoryId) params.set("categoryId", options.categoryId);
      if (options.search) params.set("search", options.search);
      if (!options.includeInactive) params.set("isActive", "true");
      const res = await fetch(`/api/menu/items?${params}`);
      if (!res.ok) throw new Error("Failed to fetch items");
      const data = await res.json();
      setItems(data.items || data.data || data);
      setTotal(data.total || (data.items || data.data || data).length);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [options.categoryId, options.search, options.includeInactive]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const createItem = async (data: Record<string, unknown>) => {
    const res = await fetch("/api/menu/items", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to create item");
    }
    await fetchItems();
    return res.json();
  };

  const updateItem = async (id: string, data: Record<string, unknown>) => {
    const res = await fetch(`/api/menu/items/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to update item");
    }
    await fetchItems();
    return res.json();
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    return updateItem(id, { isActive });
  };

  const deleteItem = async (id: string) => {
    const res = await fetch(`/api/menu/items/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || "Failed to delete item");
    }
    await fetchItems();
  };

  return { items, loading, error, total, fetchItems, createItem, updateItem, toggleActive, deleteItem };
}

export function useMenuItem(id: string | null) {
  const [item, setItem] = useState<MenuItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setItem(null);
      return;
    }
    const fetchItem = async () => {
      try {
        setLoading(true);
        const res = await fetch(`/api/menu/items/${id}`);
        if (!res.ok) throw new Error("Failed to fetch item");
        const data = await res.json();
        setItem(data.data || data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id]);

  return { item, loading, error };
}
